import { l } from '@manychat/manyui'

import { getBusinessEmail } from 'apps/email/selectors/emailSelectors'
import { FlowChartContentNodeBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartContentNodeBuilder'
import { FlowChartContentNodeHeaderBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartContentNodeHeaderBuilder'
import { FlowChartIconBuilder } from 'common/builder/components/chart/FlowChart/lib/builders/FlowChartIconBuilder'
import {
  ConverterRule,
  NodeConverterRulePayload,
} from 'common/builder/components/chart/FlowChart/lib/types'
import { getContentNodeStatTable } from 'common/builder/components/chart/FlowChart/lib/utils/getContentNodeStatTable'
import {
  DEFAULT_ICON_RENDER_SIZE,
  EmailNewConfig,
} from 'common/builder/components/chart/FlowChart/nodes/shared/config'
import { FlowChartContentNode } from 'common/builder/components/chart/FlowChart/types/FlowChartNode/FlowChartContentNode'
import { NodeType } from 'common/builder/constants/NodeType'
import {
  getRootBlock,
  getTriggerOnOpenBlock,
} from 'common/builder/emailBuilder/selectors/emailBuilderSelectors'
import { getNodeCtrUniqueUsers } from 'common/builder/models/Button/typedHelpers'
import { IEmailNode, INode } from 'common/builder/nodes/nodeInterfaces'
import { getBlocks, getButtonsMap } from 'common/builder/selectors/builder/entitySelectors'
import { getEmailSettings } from 'common/settings/selectors/emailSettingsSelectors'

import { getEmailContentFromBlocks } from './utils/getEmailContentFromBlocks'
import { getEmailNodeContentBlock } from './utils/getEmailNodeContentBlock'
import { getEmailNodeNextSteps } from './utils/getEmailNodeNextSteps'
import { getEmailNodeSenderName } from './utils/getEmailNodeSenderName'

export const ContentNodeEmailConverterRule: ConverterRule<
  INode,
  FlowChartContentNode,
  NodeConverterRulePayload
> = {
  predicate: (entity: INode) => entity?.nodeType === NodeType.EMAIL_NEW,

  convert: (
    entity: IEmailNode,
    { isStatByJSONAdsTrigger, rootNodeId, builderId, fields, state, flowChartButtons },
  ): FlowChartContentNode | null => {
    const isRootWithJSONAds = rootNodeId === entity.id && isStatByJSONAdsTrigger

    const triggerOnOpenBlock = getTriggerOnOpenBlock(state, builderId, entity.id)
    const rootBlock = getRootBlock(state, builderId, entity.id)
    const contentBlocks = rootBlock ? getBlocks(state, builderId, rootBlock?.id) : []

    const { company_name: companyName = null } = getEmailSettings(state)
    const selectedBusinessEmail = getBusinessEmail(state, entity.data.businessEmailId)
    const buttonsMap = getButtonsMap(state, builderId)

    const header = new FlowChartContentNodeHeaderBuilder(
      entity.caption,
      new FlowChartIconBuilder(EmailNewConfig.iconComponent, DEFAULT_ICON_RENDER_SIZE).build(),
    )
      .withSubtitle(EmailNewConfig.preheader || null)
      .build()

    return new FlowChartContentNodeBuilder(entity.id)
      .withNodeType(NodeType.EMAIL_NEW)
      .withHeader(header)
      .withDeliveredUniqueUsers(getNodeCtrUniqueUsers(entity))
      .withStatsTable(getContentNodeStatTable(entity, { isRootWithJSONAds }))
      .withBlocks([
        getEmailNodeContentBlock({
          rootBlockId: rootBlock?.id || null,
          sender: getEmailNodeSenderName({
            nodeSenderName: entity.data.senderName,
            companyName,
            selectedBusinessEmail,
          }),
          subject: entity.data.subject || l.translate('Enter Subject'),
          preheader: entity.data.preheader || l.translate('Enter Preheader'),
          content: getEmailContentFromBlocks(contentBlocks, {
            fields,
            buttonsMap,
            flowChartButtons,
          }),
        }),
      ])
      .withNextSteps(getEmailNodeNextSteps({ node: entity, triggerOnOpenBlock }))
      .build()
  },
}
